import { useState, useEffect } from 'react';
import StickyNav from '../home/components/StickyNav';
import Footer from '../home/components/Footer';

// Animated tropical background elements
function TropicalBg() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Gradient base */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50"></div>

      {/* Animated blobs */}
      <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-teal-200/30 blur-3xl animate-blob"></div>
      <div className="absolute top-[30%] right-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-200/25 blur-3xl animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-[10%] left-[20%] w-[400px] h-[400px] rounded-full bg-cyan-200/30 blur-3xl animate-blob animation-delay-4000"></div>

      {/* Floating leaves */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-float-leaf"
          style={{
            left: `${5 + i * 8}%`,
            top: `${10 + (i % 5) * 18}%`,
            animationDelay: `${i * 0.7}s`,
            animationDuration: `${6 + (i % 4)}s`,
            opacity: 0.18 + (i % 3) * 0.07,
          }}
        >
          <i
            className="ri-plant-line text-teal-600"
            style={{ fontSize: `${18 + (i % 4) * 10}px` }}
          ></i>
        </div>
      ))}

      {/* Floating flowers */}
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-float-slow"
          style={{
            right: `${3 + i * 11}%`,
            top: `${5 + (i % 6) * 15}%`,
            animationDelay: `${i * 1.1}s`,
            animationDuration: `${7 + (i % 3)}s`,
            opacity: 0.15 + (i % 3) * 0.06,
          }}
        >
          <i
            className="ri-flower-line text-emerald-500"
            style={{ fontSize: `${14 + (i % 3) * 8}px` }}
          ></i>
        </div>
      ))}

      {/* Wave lines at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 opacity-10">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-full">
          <path d="M0,60 C360,120 1080,0 1440,60 L1440,120 L0,120 Z" fill="#0d9488" />
        </svg>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-20 opacity-8">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-full">
          <path d="M0,40 C480,80 960,0 1440,40 L1440,80 L0,80 Z" fill="#059669" />
        </svg>
      </div>

      {/* Tropical dots pattern */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-teal-400/20 animate-pulse"
          style={{
            width: `${6 + (i % 4) * 4}px`,
            height: `${6 + (i % 4) * 4}px`,
            left: `${(i * 5.1) % 95}%`,
            top: `${(i * 7.3) % 90}%`,
            animationDelay: `${i * 0.3}s`,
            animationDuration: `${3 + (i % 3)}s`,
          }}
        ></div>
      ))}
    </div>
  );
}

export default function APropos() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative">
      {/* Tropical animated background */}
      <TropicalBg />

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.97); }
        }
        @keyframes float-leaf {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-18px) rotate(8deg); }
          50% { transform: translateY(-8px) rotate(-5deg); }
          75% { transform: translateY(-22px) rotate(10deg); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-14px) rotate(-8deg) scale(1.08); }
        }
        .animate-blob { animation: blob 8s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animate-float-leaf { animation: float-leaf 6s ease-in-out infinite; }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
      `}</style>

      <StickyNav scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/9db8f8eb12b800581950d7f0f98989ce/7a9184d8d51cefce3bb9489f9392129f.jpeg"
          alt="Restaurant Keiki Moorea"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6 max-w-4xl animate-fade-in-up">
            <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl text-white mb-6 drop-shadow-2xl">
              À Propos de Ke'iki
            </h1>
            <p className="text-xl sm:text-2xl text-white/95 font-light leading-relaxed max-w-3xl mx-auto">
              Un lieu de rencontre convivial et savoureux au bord du lagon
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Introduction */}
          <div className="mb-20">
            <h2 className="font-serif text-4xl sm:text-5xl text-gray-900 mb-8">
              Qu'est-ce qui rend Keiki Moorea spécial ?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Ke'iki n'est pas qu'un simple en-cas de bord de route ; c'est un lieu de rencontre convivial et savoureux au bord du lagon. Apprécié des locaux comme des voyageurs, son charme décontracté et sa cuisine délicieuse fidélisent sa clientèle.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              <div className="flex items-start gap-4 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Équipe locale chaleureuse</h3>
                  <p className="text-sm text-gray-600">Proposant une cuisine insulaire fraîche</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
                  <i className="ri-sun-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Terrasse décontractée</h3>
                  <p className="text-sm text-gray-600">Vue imprenable sur le lagon</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
                  <i className="ri-star-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Excellentes notes</h3>
                  <p className="text-sm text-gray-600">Sur Google et TripAdvisor</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
                  <i className="ri-restaurant-2-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Cuisine variée</h3>
                  <p className="text-sm text-gray-600">Burgers, poisson grillé, options végétaliennes</p>
                </div>
              </div>
            </div>
          </div>

          {/* Experience Section */}
          <div className="mb-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-10 sm:p-14 text-white shadow-xl">
            <h2 className="font-serif text-4xl sm:text-5xl mb-8">
              À quoi s'attendre lors d'un repas au Ke'iki
            </h2>
            <div className="space-y-4 text-lg leading-relaxed">
              <p className="flex items-start gap-3">
                <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                <span>Dans une ambiance décontractée, venez comme vous êtes !</span>
              </p>
              <p className="flex items-start gap-3">
                <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                <span>Service chaleureux et prix raisonnables</span>
              </p>
              <p className="flex items-start gap-3">
                <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                <span>Pas besoin de réserver</span>
              </p>
              <p className="flex items-start gap-3">
                <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                <span>Les plats coûtent entre 12 et 40 $ selon le menu du jour</span>
              </p>
              <p className="flex items-start gap-3">
                <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                <span>Tenue décontractée, ambiance insulaire garantie</span>
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: 'Quel type de cuisine propose Keiki Moorea ?',
                  a: "Keʻiki Moʻorea est spécialisé dans la cuisine fusion polynésienne-française, proposant des plats comme le ceviche de mahi-mahi, des salades de fruits tropicaux et des desserts d'inspiration locale, tous préparés avec les ingrédients les plus frais de l'île.",
                },
                {
                  q: "Ai-je besoin d'une réservation ?",
                  a: "Chez Keiki Moorea, aucune réservation n'est nécessaire, les clients sans réservation sont les bienvenus.",
                },
                {
                  q: 'Le menu est-il à la carte ?',
                  a: 'Oui, tous les plats ont un prix individuel et des plats du jour sont proposés.',
                },
                {
                  q: 'Acceptent-ils les réservations, et combien de temps à l\'avance ?',
                  a: "Oui, Keʻiki accepte les réservations par téléphone ou par courriel. En haute saison, nous vous recommandons de réserver au moins 3 à 5 jours à l'avance pour vous assurer une table sur la terrasse.",
                },
                {
                  q: "Quels sont les horaires d'ouverture ?",
                  a: "Keʻiki Moʻorea est ouvert tous les jours de 11h à 22h, avec des concerts de ukulélé en direct chaque soir de 18h à 20h.",
                },
                {
                  q: 'Keiki Moorea est-il adapté aux enfants ?',
                  a: "Absolument. Ce restaurant de Moorea propose un menu enfant, des chaises hautes et une ambiance décontractée qui facilitent grandement les repas avec de jeunes enfants.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white/75 backdrop-blur-sm border border-white/60 rounded-2xl p-8 hover:shadow-lg transition-shadow"
                >
                  <h3 className="font-semibold text-xl text-gray-900 mb-3 flex items-center gap-3">
                    <i className="ri-question-line text-teal-500"></i>
                    {item.q}
                  </h3>
                  <p className="text-gray-700 leading-relaxed pl-9">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
